import { supabase } from "@/integrations/supabase/client";
import {
  CreateModeloCarregadorVeicular,
  ModeloCarregadorVeicular,
  TipoCorrenteCarregador,
  UpdateModeloCarregadorVeicular,
} from "@/types/modelosCarregadores";

const BUCKET = "modelos-carregadores";

interface ModeloCarregadorDB {
  id: string;
  marca: string;
  modelo: string;
  codigo: string | null;
  tipo_corrente: string;
  modo_recarga: string | null;
  potencia_nominal_kw: number | string;
  potencia_nominal_texto: string | null;
  tensao_entrada: string | null;
  corrente_entrada: string | null;
  frequencia: string | null;
  conexao_eletrica: string | null;
  tensao_saida: string | null;
  corrente_saida: string | null;
  fator_potencia: string | null;
  eficiencia: string | null;
  tipo_conector: string | null;
  quantidade_conectores: number | null;
  comprimento_cabo: string | null;
  interface_usuario: string | null;
  modo_inicio: string | null;
  comunicacao: string | null;
  protocolo: string | null;
  medidor_energia: string | null;
  grau_protecao: string | null;
  protecao_impacto: string | null;
  protecoes_eletricas: string | null;
  normas: string | null;
  temperatura_operacao: string | null;
  umidade_operacao: string | null;
  altitude_operacao: string | null;
  dimensoes: string | null;
  peso_kg: number | string | null;
  material_involucro: string | null;
  instalacao: string | null;
  garantia_meses: number | null;
  observacoes: string | null;
  datasheet_url: string | null;
  imagem_url: string | null;
  imagem_url_2: string | null;
  created_at: string;
  updated_at: string;
}

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

function tipoCorrente(value: string): TipoCorrenteCarregador {
  return value === "CC" ? "CC" : "CA";
}

function mapRow(item: ModeloCarregadorDB): ModeloCarregadorVeicular {
  return {
    id: item.id,
    marca: item.marca || "",
    modelo: item.modelo || "",
    codigo: item.codigo,
    tipoCorrente: tipoCorrente(item.tipo_corrente),
    modoRecarga: item.modo_recarga,
    potenciaNominalKw: Number(toNum(item.potencia_nominal_kw) ?? 0),
    potenciaNominalTexto: item.potencia_nominal_texto,
    tensaoEntrada: item.tensao_entrada,
    correnteEntrada: item.corrente_entrada,
    frequencia: item.frequencia,
    conexaoEletrica: item.conexao_eletrica,
    tensaoSaida: item.tensao_saida,
    correnteSaida: item.corrente_saida,
    fatorPotencia: item.fator_potencia,
    eficiencia: item.eficiencia,
    tipoConector: item.tipo_conector,
    quantidadeConectores: item.quantidade_conectores,
    comprimentoCabo: item.comprimento_cabo,
    interfaceUsuario: item.interface_usuario,
    modoInicio: item.modo_inicio,
    comunicacao: item.comunicacao,
    protocolo: item.protocolo,
    medidorEnergia: item.medidor_energia,
    grauProtecao: item.grau_protecao,
    protecaoImpacto: item.protecao_impacto,
    protecoesEletricas: item.protecoes_eletricas,
    normas: item.normas,
    temperaturaOperacao: item.temperatura_operacao,
    umidadeOperacao: item.umidade_operacao,
    altitudeOperacao: item.altitude_operacao,
    dimensoes: item.dimensoes,
    pesoKg: toNum(item.peso_kg),
    materialInvolucro: item.material_involucro,
    instalacao: item.instalacao,
    garantiaMeses: item.garantia_meses,
    observacoes: item.observacoes,
    datasheetUrl: item.datasheet_url,
    imagemUrl: item.imagem_url,
    imagemUrl2: item.imagem_url_2,
    createdAt: item.created_at || "",
    updatedAt: item.updated_at || "",
  };
}

function mapCreateToRow(item: CreateModeloCarregadorVeicular) {
  return {
    marca: item.marca,
    modelo: item.modelo,
    codigo: item.codigo,
    tipo_corrente: item.tipoCorrente,
    modo_recarga: item.modoRecarga,
    potencia_nominal_kw: item.potenciaNominalKw,
    potencia_nominal_texto: item.potenciaNominalTexto,
    tensao_entrada: item.tensaoEntrada,
    corrente_entrada: item.correnteEntrada,
    frequencia: item.frequencia,
    conexao_eletrica: item.conexaoEletrica,
    tensao_saida: item.tensaoSaida,
    corrente_saida: item.correnteSaida,
    fator_potencia: item.fatorPotencia,
    eficiencia: item.eficiencia,
    tipo_conector: item.tipoConector,
    quantidade_conectores: item.quantidadeConectores,
    comprimento_cabo: item.comprimentoCabo,
    interface_usuario: item.interfaceUsuario,
    modo_inicio: item.modoInicio,
    comunicacao: item.comunicacao,
    protocolo: item.protocolo,
    medidor_energia: item.medidorEnergia,
    grau_protecao: item.grauProtecao,
    protecao_impacto: item.protecaoImpacto,
    protecoes_eletricas: item.protecoesEletricas,
    normas: item.normas,
    temperatura_operacao: item.temperaturaOperacao,
    umidade_operacao: item.umidadeOperacao,
    altitude_operacao: item.altitudeOperacao,
    dimensoes: item.dimensoes,
    peso_kg: item.pesoKg,
    material_involucro: item.materialInvolucro,
    instalacao: item.instalacao,
    garantia_meses: item.garantiaMeses,
    observacoes: item.observacoes,
    datasheet_url: item.datasheetUrl ?? null,
    imagem_url: item.imagemUrl ?? null,
    imagem_url_2: item.imagemUrl2 ?? null,
  };
}

function buildUpdateRow(item: UpdateModeloCarregadorVeicular): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  const campos: Array<[keyof UpdateModeloCarregadorVeicular, string]> = [
    ["marca", "marca"],
    ["modelo", "modelo"],
    ["codigo", "codigo"],
    ["tipoCorrente", "tipo_corrente"],
    ["modoRecarga", "modo_recarga"],
    ["potenciaNominalKw", "potencia_nominal_kw"],
    ["potenciaNominalTexto", "potencia_nominal_texto"],
    ["tensaoEntrada", "tensao_entrada"],
    ["correnteEntrada", "corrente_entrada"],
    ["frequencia", "frequencia"],
    ["conexaoEletrica", "conexao_eletrica"],
    ["tensaoSaida", "tensao_saida"],
    ["correnteSaida", "corrente_saida"],
    ["fatorPotencia", "fator_potencia"],
    ["eficiencia", "eficiencia"],
    ["tipoConector", "tipo_conector"],
    ["quantidadeConectores", "quantidade_conectores"],
    ["comprimentoCabo", "comprimento_cabo"],
    ["interfaceUsuario", "interface_usuario"],
    ["modoInicio", "modo_inicio"],
    ["comunicacao", "comunicacao"],
    ["protocolo", "protocolo"],
    ["medidorEnergia", "medidor_energia"],
    ["grauProtecao", "grau_protecao"],
    ["protecaoImpacto", "protecao_impacto"],
    ["protecoesEletricas", "protecoes_eletricas"],
    ["normas", "normas"],
    ["temperaturaOperacao", "temperatura_operacao"],
    ["umidadeOperacao", "umidade_operacao"],
    ["altitudeOperacao", "altitude_operacao"],
    ["dimensoes", "dimensoes"],
    ["pesoKg", "peso_kg"],
    ["materialInvolucro", "material_involucro"],
    ["instalacao", "instalacao"],
    ["garantiaMeses", "garantia_meses"],
    ["observacoes", "observacoes"],
    ["datasheetUrl", "datasheet_url"],
    ["imagemUrl", "imagem_url"],
    ["imagemUrl2", "imagem_url_2"],
  ];
  for (const [origem, destino] of campos) {
    if (item[origem] !== undefined) row[destino] = item[origem];
  }
  return row;
}

const TIPOS_IMAGEM = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

async function enviarArquivo(caminho: string, file: File, contentType: string): Promise<string> {
  const { error } = await supabase.storage.from(BUCKET).upload(caminho, file, {
    cacheControl: "3600",
    upsert: false,
    contentType,
  });
  if (error) {
    throw new Error(`Erro ao fazer upload: ${error.message}`);
  }
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(caminho);
  return data.publicUrl;
}

export const modelosCarregadoresService = {
  async uploadImagem(modeloId: string, file: File, slot: 1 | 2): Promise<string> {
    if (!TIPOS_IMAGEM.includes(file.type)) {
      throw new Error("Use uma imagem JPEG, PNG ou WebP.");
    }
    const ext = file.name.split(".").pop() || "jpg";
    const caminho = `imagens/${modeloId}_${slot}_${Date.now()}.${ext}`;
    return enviarArquivo(caminho, file, file.type);
  },

  async uploadDatasheet(modeloId: string, file: File): Promise<string> {
    const nome = file.name.toLowerCase();
    const pdf = file.type === "application/pdf" || nome.endsWith(".pdf");
    if (!pdf) {
      throw new Error("O datasheet precisa ser um arquivo PDF.");
    }
    const caminho = `datasheets/${modeloId}_${Date.now()}.pdf`;
    return enviarArquivo(caminho, file, "application/pdf");
  },

  async getAll(): Promise<ModeloCarregadorVeicular[]> {
    const { data, error } = await supabase
      .from("modelos_carregadores_veiculares")
      .select("*")
      .order("marca", { ascending: true })
      .order("modelo", { ascending: true });

    if (error) {
      throw new Error(`Erro ao buscar carregadores: ${error.message}`);
    }
    return (data || []).map((row) => mapRow(row as ModeloCarregadorDB));
  },

  async create(item: CreateModeloCarregadorVeicular): Promise<ModeloCarregadorVeicular> {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error("Sessão não encontrada. Faça login novamente.");
    }
    const { data, error } = await supabase
      .from("modelos_carregadores_veiculares")
      .insert(mapCreateToRow(item))
      .select()
      .single();
    if (error) throw new Error(error.message || "Erro ao criar carregador");
    if (!data) throw new Error("Carregador criado, mas os dados não retornaram");
    return mapRow(data as ModeloCarregadorDB);
  },

  async update(id: string, item: UpdateModeloCarregadorVeicular): Promise<ModeloCarregadorVeicular> {
    const { data, error } = await supabase
      .from("modelos_carregadores_veiculares")
      .update(buildUpdateRow(item))
      .eq("id", id)
      .select()
      .single();
    if (error) throw new Error(error.message || "Erro ao atualizar carregador");
    if (!data) throw new Error("Carregador não encontrado para atualização");
    return mapRow(data as ModeloCarregadorDB);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from("modelos_carregadores_veiculares").delete().eq("id", id);
    if (error) throw new Error(error.message || "Erro ao excluir carregador");
  },
};
